import React from 'react';
import PropTypes from 'prop-types';

/**
 * Display primitives for employee information panels.
 *
 * Extracted from EmployeeDetailInfo so the Employee tab on the User detail screen
 * renders the same way: the two screens show the same record and used to drift —
 * one with card sections, the other with flat rows and disabled form controls.
 *
 * These are read-only presentation only. Nothing here accepts input; `Toggle`
 * draws a switch rather than rendering a disabled <input>, which reads as a
 * broken control the user is not allowed to touch.
 */

/** A value, or a muted N/A placeholder when there is nothing to show. */
export const Val = ({ children, muted = false }) => {
  const isEmpty = children === null || children === undefined || children === '' || children === 'N/A';
  if (isEmpty) return <span className="tw-text-slate-300 tw-text-sm">N/A</span>;
  return (
    <span className={`tw-text-sm tw-font-medium ${muted ? 'tw-text-slate-500' : 'tw-text-primary-600'}`}>
      {children}
    </span>
  );
};

Val.propTypes = { children: PropTypes.node, muted: PropTypes.bool };

/** Label on the left at a fixed width, value on the right. */
export const InfoRow = ({ label, children }) => (
  <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-start tw-gap-0.5 sm:tw-gap-3 tw-py-1.5">
    <span className="sm:tw-w-36 sm:tw-shrink-0 tw-text-xs tw-font-medium tw-text-slate-400 tw-pt-0.5">{label}</span>
    <div className="tw-flex-1 tw-min-w-0">{children}</div>
  </div>
);

InfoRow.propTypes = { label: PropTypes.string, children: PropTypes.node };

/** A titled, colour-accented group of InfoRows. */
export const SectionPanel = ({
  icon,
  title,
  accentColor = 'tw-border-l-primary-400',
  headerBg = 'tw-bg-primary-50',
  iconColor = 'tw-text-primary-500',
  action = null,
  children,
}) => (
  <div className={`tw-rounded-lg tw-border tw-border-slate-100 tw-shadow-sm tw-overflow-hidden tw-border-l-4 ${accentColor}`}>
    <div className={`tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 ${headerBg} tw-border-b tw-border-slate-100`}>
      <span className={`${iconColor} tw-shrink-0`}>{icon}</span>
      <h6 className="tw-text-xs tw-font-bold tw-text-slate-600 tw-uppercase tw-tracking-wider tw-m-0">{title}</h6>
      {action && <div className="tw-ml-auto">{action}</div>}
    </div>
    <div className="tw-bg-white tw-px-4 tw-py-3 tw-divide-y tw-divide-slate-50">
      {children}
    </div>
  </div>
);

SectionPanel.propTypes = {
  icon:        PropTypes.node,
  title:       PropTypes.string,
  accentColor: PropTypes.string,
  headerBg:    PropTypes.string,
  iconColor:   PropTypes.string,
  action:      PropTypes.node,
  children:    PropTypes.node,
};

/** Outer white card. */
export const Card = ({ children, className = '' }) => (
  <div className={`tw-bg-white tw-rounded-2xl tw-border tw-border-slate-100 tw-shadow-sm tw-mb-4 ${className}`}>
    {children}
  </div>
);

Card.propTypes = { children: PropTypes.node, className: PropTypes.string };

/** Read-only on/off indicator drawn as a switch. */
export const Toggle = ({ checked, label }) => (
  <div className="tw-flex tw-items-center tw-gap-2">
    <div className={`tw-relative tw-inline-flex tw-h-5 tw-w-9 tw-rounded-xl tw-transition-colors ${checked ? 'tw-bg-primary-500' : 'tw-bg-slate-200'}`}>
      <span className={`tw-inline-block tw-h-4 tw-w-4 tw-rounded-xl tw-bg-white tw-shadow tw-transition-transform tw-mt-0.5 ${checked ? 'tw-translate-x-4' : 'tw-translate-x-0.5'}`} />
    </div>
    <span className="tw-text-sm tw-font-medium tw-text-slate-600">{label}</span>
  </div>
);

Toggle.propTypes = { checked: PropTypes.bool, label: PropTypes.string };

/** Gender shown the way the employee screen shows it, without live inputs. */
export const GenderRow = ({ gender }) => {
  const isMale = !gender || gender === 'male' || gender === 'M';
  return (
    <div className="tw-flex tw-items-center tw-gap-2">
      {['Male', 'Female'].map((g) => {
        const active = g === 'Male' ? isMale : !isMale;
        return (
          <span
            key={g}
            className={`tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold tw-border tw-border-solid ${
              active
                ? 'tw-bg-primary-50 tw-text-primary-700 tw-border-primary-100'
                : 'tw-bg-transparent tw-text-slate-400 tw-border-slate-100'
            }`}
          >
            {g}
          </span>
        );
      })}
    </div>
  );
};

GenderRow.propTypes = { gender: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]) };
